import React, { useState, useRef, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommanFilters from "../../components/commonComponent/CommanFilters";
import { FaFilter, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastError, toastSuccess, confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";
import { toCartArray, mapFromArray, saveCart, loadCart } from "../../utils/cartHelper";

/* ================= COLUMNS ================= */
const allFields = [
  { key: "srNo", label: "Sr.No." },
  { key: "code", label: "Item Code" },
  { key: "itemName", label: "Item Name" },
  { key: "skucode", label: "SKU Code" },
  { key: "group", label: "Group" },
  { key: "unit", label: "Unit" },
  { key: "status", label: "Status" },
  { key: "salesPrice", label: "Price (₹)" },
  { key: "offerPrice", label: "Offer Price (₹)" },
];

const forcedFields = ["srNo"];

const availabilityColors = {
  Available: { bg: "#DCF4E9", fg: "#12B76A" },
  "Not Available": { bg: "#DFE0E1", fg: "#252C32" },
};

function UserDashboard() {
  const navigate = useNavigate();
  
  /* =============== STATE =============== */
  const [cart, setCart] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    group: "",
    category: "",
    status: ""
  });
  const [filterDropdowns, setFilterDropdowns] = useState({
    groups: [],
    categories: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [user, setUser] = useState(null);
  
  const actionMenuRef = useRef(null);

  /* =============== USER & VERIFICATION =============== */
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (err) {
        toastError("Failed to load user data");
      }
    }
  }, []);

  const isVerified = (() => {
    if (!user) return false;
    if (typeof user.isVerified !== 'undefined') return Boolean(user.isVerified);
    if (typeof user.verified !== 'undefined') return Boolean(user.verified);
    const acc = user.accountStatus || user.accountstatus || user.status || "";
    return acc && String(acc).toLowerCase() === "verified";
  })();

  const isClientUser = user?.userType === "Client";
  const userStateId = user?.state?._id || user?.state || null;

  /* =============== PERMISSIONS =============== */
  const canListItem = hasPermission(user, "Item Master", "List");
  const canViewItem = hasPermission(user, "Item Master", "View");
  const canCreateItem = hasPermission(user, "Item Master", "Create");
  const canEditItem = hasPermission(user, "Item Master", "Edit");
  const canDeleteItem = hasPermission(user, "Item Master", "Delete");

  /* =============== CART MANAGEMENT =============== */
  useEffect(() => {
    try {
      const arr = loadCart();
      if (Array.isArray(arr) && arr.length > 0) {
        setCart(mapFromArray(arr));
      }
    } catch (err) {
      console.error("Error loading cart:", err);
    }
  }, []);

  const addToCart = (itemId, qty) => {
    try {
      if (isClientUser && !isVerified) {
        setShowVerificationModal(true);
        return;
      }

      const storedArr = loadCart();
      let cartArr = Array.isArray(storedArr) ? [...storedArr] : [];
      const idx = cartArr.findIndex((c) => String(c.itemId) === String(itemId));
      
      if (qty <= 0) {
        if (idx !== -1) cartArr.splice(idx, 1);
      } else {
        if (idx === -1) cartArr.push({ itemId, qty });
        else cartArr[idx].qty = qty;
      }
      
      saveCart(cartArr);
      const map = mapFromArray(cartArr);
      setCart(map);
      window.dispatchEvent(new Event('storage'));
      
      // Success feedback for adding items
      if (qty > 0) {
        toastSuccess(`Item ${qty > (cart[String(itemId)] || 0) ? 'added to' : 'updated in'} cart`);
      }
    } catch (err) {
      console.error("Cart error:", err);
      toastError('Failed to update cart');
    }
  };

  const incCart = (itemId) => {
    const current = Number(cart[String(itemId)] || 0);
    addToCart(itemId, current + 1);
  };

  const decCart = (itemId) => {
    const current = Number(cart[String(itemId)] || 0);
    const next = Math.max(0, current - 1);
    addToCart(itemId, next);
  };

  /* =============== FETCH ITEMS =============== */
  const fetchItems = async () => {
    if (!canListItem && !canViewItem && !isClientUser) {
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const qp = new URLSearchParams({ page: 1, limit: 1000 });
      
      if (isClientUser && userStateId) {
        qp.append("state", userStateId);
      }
      
      const res = await axios.get(`/admin/api/item?${qp.toString()}`);
      const list = res.data?.Items || res.data?.data || res.data || [];
      setItems(list);
    } catch (err) {
      console.error("Fetch items error:", err);
      toastError("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /* =============== FILTER ITEMS =============== */
  const filteredItems = items.filter((item) => {
    // Client user: state-based filtering
    if (isClientUser && userStateId) {
      const stateRates = Array.isArray(item.stateRate) ? item.stateRate : [];
      const hasState = stateRates.some(
        sr => String(sr.state) === String(userStateId)
      );
      if (!hasState) return false;
    }

    // Search term (apply for all users)
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (
        !item.name?.toLowerCase().includes(q) &&
        !item.code?.toLowerCase().includes(q)
      ) {
        return false;
      }
    }

    // Group filter
    if (filters.group && String(item.group?._id || item.group) !== filters.group) {
      return false;
    }

    // Category filter
    if (filters.category && String(item.category?._id || item.category) !== filters.category) {
      return false;
    }

    return true;
  });

  /* =============== DELETE =============== */
  const handleDelete = async (item) => {
    if (!canDeleteItem) return;
    
    const res = await confirmDelete(`Delete ${item.name}?`);
    if (!res.isConfirmed) return;
    
    try {
      await axios.delete(`/admin/api/item/${item._id}`);
      toastSuccess("Item deleted successfully");
      fetchItems();
    } catch (err) {
      toastError("Failed to delete item");
    }
  };

  /* =============== HELPER FUNCTIONS =============== */
  const renderStatusBadge = (label) => {
    if (!label) return "-";
    const map = availabilityColors[label] || {};
    const bg = map.bg || "#F0F0F0";
    const fg = map.fg || "#333";
    
    return (
      <span
        style={{
          backgroundColor: bg,
          color: fg,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {label}
      </span>
    );
  };

  const truncateText = (text, len = 15) => {
    if (text === null || typeof text === "undefined") return "-";
    const s = String(text);
    if (s.length <= len) return s;
    return (
      <span title={s}>
        {s.slice(0, len)}...
      </span>
    );
  };

  const isItemAvailable = (item) => {
    if (typeof item.isAvailable !== 'undefined' && item.isAvailable !== null) {
      if (typeof item.isAvailable === 'boolean') return item.isAvailable;
      const v = String(item.isAvailable).toLowerCase();
      return v === 'true' || v === '1' || v === 'yes' || v === 'available';
    }
    if (typeof item.status !== 'undefined' && item.status !== null) {
      const s = String(item.status).toLowerCase();
      return s === 'available' || s === 'true' || s === '1' || s === 'yes';
    }
    return false;
  };

  /* =============== TABLE DATA =============== */
  const tableData = filteredItems.map((item, idx) => {
    let price = "";
    if (Array.isArray(item.stateRate) && userStateId) {
      const sr = item.stateRate.find(s => String(s.state) === String(userStateId));
      price = sr?.price || "";
    }

    const isAvailableFlag = isItemAvailable(item);
    const itemQty = cart[String(item._id)] || 0;

    return {
      srNo: idx + 1,
      code: item.code,
      itemName: item.name,
      skucode: item.skucode || "",
      group: item.group?.name || item.group,
      unit: item.unit?.name || item.unit,
      status: renderStatusBadge(isAvailableFlag ? "Available" : "Not Available"),
      salesPrice: price || item.salesPrice,
      offerPrice: item.offerPrice ?? price,
      actions: (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {itemQty > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  decCart(item._id);
                }}
                aria-label="decrease"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                −
              </button>
              <span style={{ minWidth: "20px", textAlign: "center" }}>
                {itemQty}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  incCart(item._id);
                }}
                aria-label="increase"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                +
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                addToCart(item._id, 1);
              }}
              style={{
                padding: "6px 12px",
                borderRadius: "4px",
                border: "1px solid #007bff",
                background: "#007bff",
                color: "#fff",
                cursor: "pointer"
              }}
            >
              Add to Cart
            </button>
          )}
        </div>
      ),
    };
  });

  const tableColumns = [...allFields, { key: "actions", label: "Action" }];

  /* =============== PLACE ORDER =============== */
  const totalCartItems = Object.values(cart).reduce((sum, qty) => sum + Number(qty || 0), 0);

  const handlePlaceOrder = () => {
    if (isClientUser && !isVerified) {
      setShowVerificationModal(true);
      return;
    }

    // Check if cart is empty
    if (totalCartItems === 0) {
      toastError("Please add items to cart before placing order");
      return;
    }

    try {
      const cartArrLocal = loadCart();
      const cartArrFromState = toCartArray(cart);
      const preferred = cartArrFromState.length > 0 ? cartArrFromState : cartArrLocal;
      
      if (!preferred || preferred.length === 0) {
        toastError("Cart is empty. Please add items first.");
        return;
      }
      
      navigate('/user/order/create', { state: { cartArr: preferred, cartArrLocal } });
    } catch (err) {
      console.error("Place order error:", err);
      toastError("Failed to process order. Please try again.");
    }
  };

  /* =============== RENDER =============== */
  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Items</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: 250,
              padding: "8px 10px",
              border: "1px solid #ddd",
              borderRadius: "4px"
            }}
          />

          {isClientUser ? (
            <button
              onClick={handlePlaceOrder}
              disabled={totalCartItems === 0}
              style={{
                padding: "10px 20px",
                background: totalCartItems === 0 ? "#6c757d" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: totalCartItems === 0 ? "not-allowed" : "pointer",
                position: "relative",
                opacity: totalCartItems === 0 ? 0.6 : 1
              }}
            >
              Place Order
              {totalCartItems > 0 && (
                <span style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-8px",
                  background: "#dc3545",
                  color: "#fff",
                  borderRadius: "50%",
                  padding: "2px 6px",
                  fontSize: "12px"
                }}>
                  {totalCartItems}
                </span>
              )}
            </button>
          ) : (
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowFilters(s => !s)}
                style={{
                  padding: "10px 20px",
                  background: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                <FaFilter /> Filter
              </button>
              {canCreateItem && (
                <button
                  onClick={() => navigate("/admin/item/create")}
                  style={{
                    padding: "10px 20px",
                    background: "#28a745",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <FaPlus /> Add New Item
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* search is moved to header */}

      {!isClientUser && showFilters && (
        <CommanFilters
          filters={filters}
          dropdowns={filterDropdowns}
          onFilterChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))}
        />
      )}

      <CommonTable
        columns={tableColumns}
        data={tableData}
        loading={loading}
      />

      {showVerificationModal && (
        <>
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <div style={{
              background: "#fff",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "500px",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "48px", marginBottom: "20px" }}>⚠️</div>
              <h3>Action Required</h3>
              <p>Your Account Is Not Verified. Please Fill All Required Information And Complete Verification To Place An Order.</p>
              <div style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "center" }}>
                <button
                  onClick={() => {
                    setShowVerificationModal(false);
                    const uid = user?._id || user?.id || "";
                    if (uid) navigate(`/user/client/edit/${uid}`);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  Complete Verification
                </button>
                <button
                  onClick={() => setShowVerificationModal(false)}
                  style={{
                    padding: "10px 20px",
                    background: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserDashboard;
